import React, { useMemo } from "react";
import ReactSelect from "react-select";
import styles from "./Select.module.css";

/**
 * Global Select Component adhering to the AsmitA Portal Theme
 * Powered by a custom floating menu (no browser default OS popups!)
 *
 * @param {Array<string|{value: string|number, label: string}>} options
 * @param {string|number} value
 * @param {function} onChange - Supports both (e) => e.target.value and (value) => value
 * @param {string} label - Optional label above select
 * @param {string} placeholder - Optional default placeholder text
 * @param {string} size - 'sm' | 'md' | 'lg' (default 'md')
 * @param {string} error - Optional error text
 * @param {boolean} disabled / isDisabled
 * @param {boolean} isSearchable
 * @param {boolean} isClearable
 */
const Select = ({
  options = [],
  value,
  onChange,
  label,
  placeholder = "Select...",
  size = "md",
  error,
  disabled = false,
  isDisabled = false,
  className = "",
  style = {},
  id,
  name,
  required = false,
  isClearable = false,
  isSearchable = true,
  children,
  ...rest
}) => {
  // Normalize options from either options array or JSX children
  const normalizedOptions = useMemo(() => {
    if (children) {
      const extracted = [];
      React.Children.forEach(children, (child) => {
        if (!child) return;
        if (child.type === "option") {
          extracted.push({
            value: child.props.value !== undefined ? child.props.value : child.props.children,
            label: child.props.children || String(child.props.value || ""),
          });
        }
      });
      if (extracted.length > 0) return extracted;
    }

    if (!Array.isArray(options)) return [];
    return options.map((opt) => {
      if (typeof opt === "object" && opt !== null) {
        return {
          value: opt.value !== undefined ? opt.value : (opt.id !== undefined ? opt.id : ""),
          label: opt.label !== undefined ? opt.label : opt.name || String(opt.value ?? ""),
        };
      }
      return { value: opt, label: String(opt ?? "") };
    });
  }, [options, children]);

  // Match current selected value
  const selectedOption = useMemo(() => {
    if (value === null || value === undefined) return null;
    if (typeof value === "object" && "value" in value) return value;
    const strVal = String(value);
    const found = normalizedOptions.find((opt) => String(opt.value) === strVal);
    if (found) return found;
    if (strVal === "") {
      return normalizedOptions.find((opt) => opt.value === "") || null;
    }
    return { label: strVal, value };
  }, [normalizedOptions, value]);

  const handleSelectChange = (selected) => {
    const nextVal = selected ? selected.value : "";
    if (!onChange) return;
    const syntheticEvent = {
      target: { name: name || "", value: nextVal },
      currentTarget: { name: name || "", value: nextVal },
      value: nextVal,
      toString: () => String(nextVal),
      valueOf: () => nextVal,
    };
    onChange(syntheticEvent, nextVal);
  };

  const isDisabledState = disabled || isDisabled;

  // Custom AsmitA Portal Theme styling for react-select
  const customStyles = useMemo(() => {
    const isSm = size === "sm";
    const isLg = size === "lg";

    return {
      container: (base) => ({
        ...base,
        width: "100%",
      }),
      control: (base, state) => ({
        ...base,
        width: "100%",
        minHeight: isSm ? "32px" : isLg ? "44px" : "38px",
        height: isSm ? "32px" : "auto",
        borderRadius: isSm ? "8px" : "10px",
        borderColor: error
          ? "#ef4444"
          : state.isFocused
          ? "var(--color-dark-bg, #27306f)"
          : "#cbd5e1",
        boxShadow: state.isFocused
          ? "0 0 0 3px rgba(39, 48, 111, 0.15)"
          : "none",
        "&:hover": {
          borderColor: error ? "#ef4444" : "var(--color-dark-bg, #27306f)",
        },
        backgroundColor: isDisabledState ? "#f8fafc" : "#ffffff",
        fontSize: isSm ? "12px" : "13px",
        fontFamily: "'Poppins', 'Montserrat', -apple-system, sans-serif",
        cursor: isDisabledState ? "not-allowed" : "pointer",
        transition: "all 0.15s ease-in-out",
        ...style,
      }),
      valueContainer: (base) => ({
        ...base,
        padding: isSm ? "0 8px" : "2px 10px",
      }),
      singleValue: (base) => ({
        ...base,
        color: "#0f172a",
        fontWeight: "500",
      }),
      placeholder: (base) => ({
        ...base,
        color: "#94a3b8",
        fontSize: isSm ? "12px" : "13px",
      }),
      indicatorSeparator: () => ({
        display: "none",
      }),
      dropdownIndicator: (base, state) => ({
        ...base,
        color: state.isFocused ? "var(--color-dark-bg, #27306f)" : "#64748b",
        padding: isSm ? "2px 6px" : "4px 8px",
        "&:hover": {
          color: "var(--color-dark-bg, #27306f)",
        },
        transition: "transform 0.2s ease, color 0.15s ease",
        transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : "rotate(0deg)",
      }),
      clearIndicator: (base) => ({
        ...base,
        padding: isSm ? "2px" : "4px",
        cursor: "pointer",
      }),
      menuPortal: (base) => ({
        ...base,
        zIndex: 99999,
      }),
      menu: (base) => ({
        ...base,
        zIndex: 99999,
        borderRadius: "10px",
        boxShadow:
          "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.08)",
        border: "1px solid #e2e8f0",
        padding: "4px",
        backgroundColor: "#ffffff",
        animation: "fadeIn 0.12s ease-out",
      }),
      menuList: (base) => ({
        ...base,
        padding: "4px",
        maxHeight: "260px",
      }),
      option: (base, state) => ({
        ...base,
        borderRadius: "6px",
        marginBottom: "4px",
        "&:last-child": {
          marginBottom: "0",
        },
        padding: isSm ? "6px 10px" : "8px 12px",
        fontSize: isSm ? "12px" : "13px",
        fontFamily: "'Poppins', 'Montserrat', -apple-system, sans-serif",
        fontWeight: state.isSelected ? "600" : "400",
        backgroundColor: state.isSelected
          ? "var(--color-dark-bg, #27306f)"
          : state.isFocused
          ? "#f1f5f9"
          : "transparent",
        color: state.isSelected ? "#ffffff" : "#1e293b",
        cursor: "pointer",
        transition: "background 0.12s ease",
        "&:active": {
          backgroundColor: "var(--color-dark-bg, #27306f)",
          color: "#ffffff",
        },
      }),
    };
  }, [size, error, isDisabledState, style]);

  const selectElement = (
    <div className={`${styles.selectContainer} ${className}`} style={{ width: "100%" }}>
      <ReactSelect
        id={id}
        name={name}
        value={selectedOption}
        onChange={handleSelectChange}
        options={normalizedOptions}
        isDisabled={isDisabledState}
        isClearable={isClearable}
        isSearchable={isSearchable}
        placeholder={placeholder}
        menuPlacement="auto"
        menuPosition="fixed"
        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
        styles={customStyles}
        {...rest}
      />
      {name && (
        <input
          type="hidden"
          name={name}
          value={selectedOption?.value ?? value ?? ""}
        />
      )}
    </div>
  );

  if (!label && !error) {
    return selectElement;
  }

  return (
    <div className={styles.wrapper}>
      {label && (
        <label htmlFor={id} className={styles.label}>
          {label} {required && <span style={{ color: "#ef4444" }}>*</span>}
        </label>
      )}
      {selectElement}
      {error && <span className={styles.errorMessage}>{error}</span>}
    </div>
  );
};

export default Select;
